import { useMemo, useState } from 'react';
import { trpc } from '@/app/api/trpc/client';
import { useSettingsStore } from '@/app/stores/settingsStore';

function getCoachSpecialty(
  defaultPlan: { goal: string | null; raceDistance: string | null } | null
): string | null {
  if (!defaultPlan?.goal) return null;
  if (defaultPlan.goal === 'Triathlon') {
    return defaultPlan.raceDistance === 'FullIronman'
      ? 'Ironman'
      : defaultPlan.raceDistance === 'HalfIronman'
        ? 'Half Ironman'
        : 'Triathlon';
  }
  return defaultPlan.goal.replace(/([a-z])([A-Z])/g, '$1 $2');
}

export const CoachSelectionCard = () => {
  const utils = trpc.useUtils();
  const { trainingSettings, setTrainingSettings } = useSettingsStore();
  const { data: coaches, isLoading } = trpc.settings.getCoaches.useQuery();
  const [pendingCoachId, setPendingCoachId] = useState<string | null>(null);

  const { mutate: updateCoachSelection, status: updateCoachSelectionStatus } =
    trpc.settings.updateCoach.useMutation({
      onSuccess: async () => {
        await Promise.all([utils.settings.getTrainingSettings.invalidate()]);
        const updatedSettings =
          await utils.settings.getTrainingSettings.fetch();
        if (updatedSettings) {
          setTrainingSettings(updatedSettings);
        }
      },
      onSettled: () => {
        setPendingCoachId(null);
      },
    });

  const currentCoachId = trainingSettings?.coachProfileId || null;
  const selectedCoach = useMemo(
    () => coaches?.find((coach) => coach.id === currentCoachId),
    [coaches, currentCoachId]
  );

  const disableCoachSelectionCard = Boolean(
    currentCoachId && trainingSettings?.onboardingComplete === false
  );

  const isUpdatingCoach = updateCoachSelectionStatus === 'pending';

  const notifyCoach = (coachName: string, coachId: string) => {
    const input = document.getElementById('chat-input');
    if (input instanceof HTMLInputElement) {
      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value'
      )?.set;
      nativeInputValueSetter?.call(
        input,
        `I have selected the coach: ${coachName} (ID: ${coachId}). Please proceed with my onboarding.`
      );
      input.dispatchEvent(new Event('input', { bubbles: true }));
      setTimeout(
        () =>
          input.form?.dispatchEvent(
            new Event('submit', { cancelable: true, bubbles: true })
          ),
        10
      );
    }
  };

  const handleCoachSelection = (coachId: string, coachName: string) => {
    if (disableCoachSelectionCard) {
      return;
    }
    if (currentCoachId === coachId) {
      notifyCoach(coachName, coachId);
      return;
    }
    setPendingCoachId(coachId);
    updateCoachSelection(
      { coachProfileId: coachId },
      {
        onSuccess: () => {
          notifyCoach(coachName, coachId);
        },
      }
    );
  };

  const buttonDisabled = isUpdatingCoach || disableCoachSelectionCard;

  return (
    <div className='w-full p-4 border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-900'>
      <div className='flex items-center justify-between mb-3'>
        <div className='text-sm font-semibold text-slate-900 dark:text-slate-100'>
          Choose Coach
        </div>
        {selectedCoach && (
          <div className='text-xs uppercase tracking-wider text-blue-600 dark:text-blue-400 hidden'>
            {selectedCoach.priceTier || 'Default'}
          </div>
        )}
      </div>
      {disableCoachSelectionCard && (
        <div className='mb-3 text-xs text-slate-500 dark:text-slate-400'>
          Coach selection is locked while we finish onboarding—thanks for your
          patience.
        </div>
      )}
      {isLoading ? (
        <div className='text-xs text-slate-500'>Loading coaches...</div>
      ) : coaches && coaches.length > 0 ? (
        <div className='grid gap-3 sm:grid-cols-2 lg:grid-cols-3'>
          {coaches.map((coach) => {
            const isSelected = coach.id === currentCoachId;
            const isPending = pendingCoachId === coach.id && isUpdatingCoach;
            const specialty = getCoachSpecialty(coach.defaultPlan);
            return (
              <button
                key={coach.id}
                type='button'
                onClick={() => handleCoachSelection(coach.id, coach.name)}
                disabled={buttonDisabled}
                aria-disabled={disableCoachSelectionCard}
                className={`flex flex-col gap-2 rounded-lg border p-3 text-left transition-all ${
                  isSelected
                    ? 'border-blue-600 bg-blue-600/10 dark:bg-blue-500/10'
                    : disableCoachSelectionCard
                      ? 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/60'
                      : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800'
                } ${disableCoachSelectionCard ? 'cursor-not-allowed opacity-70' : ''}`}
              >
                <div className='flex items-start gap-2'>
                  {coach.avatarUrl ? (
                    <img
                      src={coach.avatarUrl}
                      alt={coach.name}
                      className='h-8 w-8 rounded-full object-cover'
                    />
                  ) : (
                    <div className='flex h-8 w-8 items-center justify-center rounded-full bg-slate-200 text-xs font-semibold uppercase text-slate-600 dark:bg-slate-700 dark:text-slate-200'>
                      {coach.name.charAt(0)}
                    </div>
                  )}
                  <div>
                    <div className='text-sm font-semibold text-slate-900 dark:text-slate-100'>
                      {coach.name}
                    </div>
                    <div className='text-xs text-slate-500 dark:text-slate-400'>
                      {coach.shortDescription || coach.bio || ''}
                    </div>
                    {specialty && (
                      <span className='mt-1 inline-block px-2 py-0.5 rounded-full text-[11px] font-medium border bg-transparent text-emerald-500 border-emerald-500 dark:text-emerald-300 dark:border-emerald-400'>
                        {specialty}
                      </span>
                    )}
                  </div>
                </div>
                {(isPending || isSelected) && (
                  <div className='text-xs text-slate-500 dark:text-slate-400'>
                    {isPending ? 'Saving...' : 'Selected'}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      ) : (
        <div className='text-xs text-slate-500'>No coaches available yet.</div>
      )}
    </div>
  );
};
